import { Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import React from 'react';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import Colors from '../../utils/color';
import CustomerPulse from '../CustomerPulse';
import LiveTranscriptCard from './LiveTranscriptCard';
import useLeftStackCollapse from './hooks/useLeftStackCollapse.hooks';

const useStyles = makeStyles<Theme>(({ spacing }) => ({
  leftPane: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    // Left card gets top space; only the center attaches to the header.
    paddingTop: spacing(1),
  },
  // Single left card: transcript section + reminder section + AI status bar,
  // each an internal white card under its heading (mirrors the mockup).
  leftCard: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#F9FAFB',
    border: `${spacing(0.125)} solid ${Colors.alto}`,
    borderRadius: spacing(1),
    padding: spacing(1.5),
  },
  // Wraps each card inside a vertical panel: fills the panel height so each
  // card's OWN internal scroll works. overflow hidden (not auto) — the cards
  // scroll internally now, so this wrapper must never show its own (outer)
  // scrollbar, which otherwise appeared beside a collapsed hug-height card.
  leftStackPane: {
    height: '100%',
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'hidden',
    overflowX: 'hidden',
  },
  // Horizontal draggable divider between the stacked left-pane cards (Customer
  // Pulse / Live Transcript / Action Center).
  vResizeHandle: {
    height: spacing(1.5),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    outline: 'none',
    '&::after': {
      content: '""',
      height: 3,
      width: spacing(5),
      borderRadius: 2,
      backgroundColor: Colors.silverChalice,
      transition: 'background-color 120ms ease',
    },
    '&:hover::after, &[data-resize-handle-active]::after': {
      backgroundColor: Colors.readBack,
    },
  },
}));

interface LeftSidebarProps {
  // Per-agent storage key; the nested vertical stack persists under a suffix.
  paneStorageKey: string;
}

/**
 * LEFT SIDEBAR of the HITL console. One card holding a drag-to-resizable
 * vertical stack of Customer Pulse + Live Transcript (sizes persist per agent
 * via autoSaveId). The Pulse strip folds in the metrics, signal/reminder chips
 * and the AI co-pilot ask (formerly the separate Action Center card), so the
 * Transcript gets the bulk of the height. On an unsupervised AI task arrival the
 * left PANE widens to 50% (see usePaneLayout) — keeping menu + cart visible —
 * while supervised keeps the agent's saved pane sizes. Collapse-sync between each
 * card's chevron and its panel lives in useLeftStackCollapse.
 */
const LeftSidebar = ({ paneStorageKey }: LeftSidebarProps) => {
  const classes = useStyles();
  const { cpOpen, setCpOpen, ltOpen, setLtOpen, cpPanelRef, ltPanelRef } =
    useLeftStackCollapse();

  return (
    <div className={classes.leftPane}>
      <div className={classes.leftCard}>
        {/* Left-pane cards (Customer Pulse strip + Live Transcript) are
            drag-to-resizable; sizes persist per agent via autoSaveId. A distinct
            key (`:left-stack-2z`) from the old 3-card stack so previously saved
            three-panel sizes don't leak into this two-panel layout. */}
        <PanelGroup
          direction="vertical"
          autoSaveId={`${paneStorageKey}:left-stack-2z`}
        >
          {/* collapsedSize is larger than the transcript's (7): the collapsed
              Pulse strip keeps its AI status band below the header, so it needs
              ~2x the height. Stays below minSize so collapse still snaps in. */}
          <Panel
            id="cp"
            order={1}
            ref={cpPanelRef}
            collapsible
            collapsedSize={11}
            defaultSize={24}
            minSize={14}
            onCollapse={() => setCpOpen(false)}
            onExpand={() => setCpOpen(true)}
          >
            <div className={classes.leftStackPane}>
              <CustomerPulse open={cpOpen} onOpenChange={setCpOpen} />
            </div>
          </Panel>
          <PanelResizeHandle className={classes.vResizeHandle} />
          <Panel
            id="lt"
            order={2}
            ref={ltPanelRef}
            collapsible
            collapsedSize={7}
            defaultSize={76}
            minSize={20}
            onCollapse={() => setLtOpen(false)}
            onExpand={() => setLtOpen(true)}
          >
            <div className={classes.leftStackPane}>
              <LiveTranscriptCard open={ltOpen} onOpenChange={setLtOpen} />
            </div>
          </Panel>
        </PanelGroup>
      </div>
    </div>
  );
};

export default LeftSidebar;
